import { AnimatePresence } from 'framer-motion';
import moment from 'moment';
import { useMemo, useRef } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { Box, Flex } from 'reflexbox';

import { useFeature, useFillViewportHeight, useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Container, Spinner, tokens } from '@treams/treams-ui';

import TalentReviewCurrentSelect from './components/TalentReviewCurrentSelect';
import TalentReviewDateNavigation from './components/TalentReviewDateNavigation';
import TalentReviewGridActionsMenu from './components/TalentReviewGridActionsMenu';
import TalentReviewPageHeader from './components/TalentReviewPageHeader';
import TalentReviewSectionTitle from './components/TalentReviewSectionTitle';
import TalentReviewSidebar from './components/TalentReviewSidebar';
import TalentReviewSplitLayout from './components/TalentReviewSplitLayout';
import TalentReviewUnplaced from './components/TalentReviewUnplaced';
import useTalentReviewPath from './hooks/useTalentReviewPath';

const noop = () => undefined;

/**
 * Shown while a newly selected template's grid is still loading. The previous
 * grid belongs to another template, so its cells stay hidden, but the page
 * around them does not: the group header, the date controls, the unplaced
 * pool and the team sidebar all stay where they were.
 * @param props component props
 * @param props.previous the grid that was on screen before the switch
 * @param props.templateID the template now selected in the URL
 * @param props.groupID the group, unchanged by a template switch
 * @param props.localDate the grid day, unchanged by a template switch
 */
export default function TalentReviewPending(props: {
	previous: graphql.TalentReviewGrid;
	templateID: string;
	groupID: string;
	localDate: graphql.Scalars['LocalDate']['input'];
}) {
	const { localize } = useLocalize();
	const path = useTalentReviewPath();
	const hideOwnerInTalentReview = useFeature(graphql.FeatureName.HideOwnerInTalentReview);
	const splitRef = useRef<HTMLDivElement>(null);
	useFillViewportHeight(splitRef);

	const today = moment().format('YYYY-MM-DD');

	const templatesQuery = graphql.useTalentReviewTemplatesQuery({
		variables: { isArchived: false, first: 1000 },
	});

	const templateName = useMemo(() => {
		const incoming = templatesQuery.data?.talentReviewTemplates.edges.find(
			({ node }) => node.id === props.templateID,
		)?.node;

		return (
			(incoming ? localize(incoming.title).text : undefined) ??
			localize(props.previous.template.title).text ??
			''
		);
	}, [localize, props.previous.template.title, props.templateID, templatesQuery.data]);

	const users = useMemo(() => {
		return props.previous.group.members.edges
			.filter(({ role }) => role.type !== graphql.RoleType.Owner || !hideOwnerInTalentReview)
			.map(({ node }) => node as graphql.User);
	}, [hideOwnerInTalentReview, props.previous.group.members.edges]);

	const unplacedUsers = useMemo(() => {
		return users.filter(
			(user) => !props.previous.placements.some((placement) => placement.user.id === user.id),
		);
	}, [props.previous.placements, users]);

	const activity = useMemo(
		() =>
			props.previous.activity.filter(
				(item) => moment(item.createdAt).format('YYYY-MM-DD') <= props.localDate,
			),
		[props.localDate, props.previous.activity],
	);

	const showPool = props.localDate === today && unplacedUsers.length > 0;

	return (
		<TalentReviewSplitLayout
			ref={splitRef}
			sidebar={
				<TalentReviewSidebar
					localDate={props.previous.localDate}
					template={props.previous.template}
					placements={props.previous.placements}
					movedUserIDs={[]}
					readonly
					focusKey={0}
					onSelectUser={noop}
					onRequestFocus={noop}
					onUpdateMotivation={noop}
					onFlushMotivation={noop}
					onUpdatePlacement={noop}
					onMovePlacement={noop}
					onUndoMove={noop}
				/>
			}
		>
			<Container py={4}>
				<Box mb={4}>
					<TalentReviewPageHeader
						backLink={path()}
						title={props.previous.group.name}
						groupName={props.previous.group.name}
						templateID={props.templateID}
						groupID={props.groupID}
						localDate={props.localDate}
					/>
				</Box>
				<Flex my={4} alignItems="center" justifyContent="space-between">
					<Flex alignItems="center" style={{ gap: tokens.gap.group }} minWidth={0}>
						{templateName && <TalentReviewSectionTitle>{templateName}</TalentReviewSectionTitle>}
						{props.localDate === today && (
							<TalentReviewCurrentSelect
								groupID={props.groupID}
								templateID={props.templateID}
								localDate={props.localDate}
							/>
						)}
					</Flex>
					<Flex alignItems="center" style={{ gap: tokens.gap.inline }} flexShrink={0}>
						<TalentReviewDateNavigation
							groupID={props.groupID}
							templateID={props.templateID}
							localDate={props.localDate}
							activity={activity}
						/>
						<TalentReviewGridActionsMenu
							clearDisabled
							onClearGrid={noop}
							groupID={props.groupID}
							templateID={props.templateID}
							templateName={templateName}
							localDate={props.localDate}
						/>
					</Flex>
				</Flex>
				<DragDropContext onDragEnd={noop}>
					<AnimatePresence initial={false}>
						{showPool && <TalentReviewUnplaced key="unplaced" users={unplacedUsers} readonly />}
					</AnimatePresence>
				</DragDropContext>
				<Flex my={5} justifyContent="center">
					<Spinner />
				</Flex>
			</Container>
		</TalentReviewSplitLayout>
	);
}
